import React, { useState, useEffect } from 'react';
import { XMarkIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { freezoneService, cityService } from '../../services/cityService';

const FreezoneForm = ({ freezone, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [benefits, setBenefits] = useState([]);
  const [cities, setCities] = useState([]);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch
  } = useForm({
    defaultValues: {
      name: '',
      cityId: '',
      status: 'active',
      timeline: '',
      offer: '',
      newBenefit: ''
    }
  });

  useEffect(() => {
    fetchCities();
    if (freezone) {
      setValue('name', freezone.name);
      setValue('cityId', freezone.cityId || freezone.city_id);
      setValue('status', freezone.status);
      setValue('timeline', freezone.timeline);
      setValue('offer', freezone.offer || freezone.special_offer);
      setBenefits(freezone.benefits || []);
      setImagePreview(freezone.image_url || freezone.image_url);
    }
  }, [freezone, setValue]);

  const fetchCities = async () => {
    try {
      const response = await cityService.getCities();
      setCities(response.cities || response);
    } catch (error) {
      console.error('Error fetching cities:', error);
      toast.error('Failed to load cities');
      setCities([]);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select a valid image file');
        return;
      }

      setSelectedImageFile(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const addBenefit = () => {
    const newBenefit = watch('newBenefit').trim();
    if (newBenefit && !benefits.includes(newBenefit)) {
      setBenefits([...benefits, newBenefit]);
      setValue('newBenefit', '');
    } else if (benefits.includes(newBenefit)) {
      toast.error('This benefit already exists');
    }
  };

  const removeBenefit = (index) => {
    setBenefits(benefits.filter((_, i) => i !== index));
  };

  const onSubmit = async (data) => {
    if (benefits.length === 0) {
      toast.error('Please add at least one benefit');
      return;
    }

    if (!data.cityId) {
      toast.error('Please select a city');
      return;
    }

    // Validate that the selected city exists
    const selectedCity = cities.find(city => city.id === data.cityId);
    if (!selectedCity) {
      toast.error('Selected city not found. Please refresh and try again.');
      return;
    }

    setLoading(true);
    try {
      console.log('Form data:', data);
      console.log('Selected city ID:', data.cityId, 'Type:', typeof data.cityId);
      console.log('Available cities:', cities);
      
      const freezoneData = {
        name: data.name,
        city_id:  data.cityId , 
        status: data.status,
        timeline: data.timeline,
        special_offer: data.offer,
        benefits
      };
      
      console.log('Freezone data being sent:', freezoneData);

      // Add image file if selected
      if (selectedImageFile) {
        freezoneData.image = selectedImageFile;
      } else if (freezone && (freezone.image || freezone.image_url)) {
        // Keep existing image URL for updates
        freezoneData.image = freezone.image || freezone.image_url;
      }

      if (freezone) {
        await freezoneService.updateFreezone(freezone.id, freezoneData);
        toast.success('Freezone updated successfully');
      } else {
        await freezoneService.createFreezone(freezoneData);
        toast.success('Freezone created successfully');
      }
      
      onSuccess();
    } catch (error) {
      console.error('Error saving freezone:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      // Show more specific error message
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to save freezone';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium text-gray-900">
            {freezone ? 'Edit Freezone' : 'Add New Freezone'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Freezone Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Freezone Name *
            </label>
            <input
              type="text"
              {...register('name', { required: 'Freezone name is required' })}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                errors.name ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Enter freezone name (e.g., IFZA, SHAMS)"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
            )}
          </div>

          {/* City Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              City *
            </label>
            <select
              {...register('cityId', { required: 'Please select a city' })}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                errors.cityId ? 'border-red-300' : 'border-gray-300'
              }`}
            >
              <option value="">Select a city</option>
              {cities.map((city) => (
                <option key={city.id} value={city.id}>
                  {city.name}
                </option>
              ))}
            </select>
            {errors.cityId && (
              <p className="mt-1 text-sm text-red-600">{errors.cityId.message}</p>
            )}
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              {...register('status')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {/* Timeline */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Processing Timeline
            </label>
            <input
              type="text"
              {...register('timeline')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., 4-6 working days"
            />
            <p className="mt-1 text-sm text-gray-500">
              How long does it take to process applications in this freezone?
            </p>
          </div>

          {/* Special Offer */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Special Offer
            </label>
            <input
              type="text"
              {...register('offer')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., 🎉 Free Visa For Life!"
            />
            <p className="mt-1 text-sm text-gray-500">
              Any special promotional offers for this freezone (optional)
            </p>
          </div>

          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Freezone Logo
            </label>
            <div className="flex items-center space-x-4">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              {imagePreview && (
                <div className="flex-shrink-0">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="h-16 w-16 rounded-lg object-cover border border-gray-200"
                  />
                </div>
              )}
            </div>
            <p className="mt-1 text-sm text-gray-500">
              Upload a logo or image for this freezone (max 5MB)
            </p>
          </div>

          {/* Benefits */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Freezone Benefits *
            </label>
            <div className="space-y-3">
              {/* Add new benefit */}
              <div className="flex space-x-2">
                <input
                  type="text"
                  {...register('newBenefit')}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Add a new benefit (you can use **text** for bold formatting)"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addBenefit();
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={addBenefit}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <PlusIcon className="h-4 w-4" />
                </button>
              </div>

              {/* Benefits list */}
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start justify-between p-3 bg-gray-50 rounded-md">
                    <div className="flex-1 mr-3">
                      <span 
                        className="text-sm text-gray-700"
                        dangerouslySetInnerHTML={{ 
                          __html: benefit.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') 
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeBenefit(index)}
                      className="text-red-600 hover:text-red-800 flex-shrink-0"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>

              {benefits.length === 0 && (
                <div className="text-center py-4 bg-gray-50 rounded-md">
                  <p className="text-sm text-gray-500">No benefits added yet. Add at least one benefit.</p>
                </div>
              )}

              <div className="text-xs text-gray-500">
                <p><strong>Tip:</strong> Use **text** to make parts of your benefits bold (e.g., **Dubai Address**: Your business will have a prestigious Dubai address.)</p>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : (freezone ? 'Update Freezone' : 'Create Freezone')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FreezoneForm; 